import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Alert, Snackbar } from "@mui/material";
import { ApiError } from "@/api/client";

type Severity = "success" | "error" | "info" | "warning";

interface ToastState {
  notify: (message: string, severity?: Severity) => void;
  /** Report a failure using the upstream's own words, which are usually better. */
  failed: (error: unknown, fallback?: string) => void;
}

const ToastContext = createContext<ToastState | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<{ message: string; severity: Severity } | null>(null);

  const notify = useCallback((message: string, severity: Severity = "success") => {
    setCurrent({ message, severity });
  }, []);

  const failed = useCallback((error: unknown, fallback = "That did not work") => {
    const message =
      error instanceof ApiError ? error.message
      : error instanceof Error ? error.message
      : fallback;
    setCurrent({ message, severity: "error" });
  }, []);

  const value = useMemo(() => ({ notify, failed }), [notify, failed]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={!!current}
        autoHideDuration={current?.severity === "error" ? 8000 : 4000}
        onClose={() => setCurrent(null)}
        // At the top, under the header: at the bottom it covered chat boxes and
        // the buttons at the foot of dialogs.
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: { xs: 64, sm: 72 } }}
      >
        {current ? (
          <Alert severity={current.severity} onClose={() => setCurrent(null)} variant="filled">
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastState {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}
